import React, { useCallback } from 'react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Icon, IIconStyles } from '@fluentui/react/lib/Icon';
import { Separator } from '@fluentui/react/lib/Separator';
import AdminHelpFAQ from './AdminHelpFAQ';
import { DefaultButton, PrimaryButton } from '@fluentui/react';
import { createNewGuid } from '../../../../../components/Utils';
import { FrequentQuestion } from '../../../../../api/generated/data-contracts';
import { useFaqs } from '../useFaqs';
import { useSetQueryData } from '../../../../../hooks/useSetQueryData';
import { useMutation } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';

function AdminFAQs() {
    const { data: fAQs, queryKey } = useFaqs({ suspense: true });
    const setFAQs = useSetQueryData<typeof fAQs>(queryKey);

    const ReorderFAQ = useCallback(
        (s: FrequentQuestion, direction: number) => {
            setFAQs(faqs => {
                let newOrder: number = -1;

                if (direction > 0) {
                    newOrder = s.order < faqs.length ? s.order + 1 : faqs.length;
                } else {
                    newOrder = s.order > 1 ? s.order - 1 : 1;
                }

                let orderNumber: number = 1;
                return faqs
                    .map(faq => {
                        if (newOrder === orderNumber) {
                            orderNumber = orderNumber + 1;
                        }
                        if (faq.id === s.id) {
                            return {
                                ...faq,
                                order: newOrder,
                            };
                        }
                        return {
                            ...faq,
                            order: orderNumber++,
                        };
                    })
                    .sort((a, b) => {
                        return a.order < b.order ? -1 : 1;
                    });
            });
        },
        [setFAQs],
    );

    const RemoveFAQ = useCallback(
        (s: FrequentQuestion) => {
            setFAQs(faqs => faqs.filter(vid => vid.id !== s.id));
        },
        [setFAQs],
    );

    const FAQUpdated = useCallback(
        (id: string, header: string, description: string) => {
            setFAQs(faqs =>
                faqs.map(faq => {
                    if (faq.id === id) {
                        return {
                            ...faq,
                            question: header,
                            response: description,
                        };
                    }
                    return faq;
                }),
            );
        },
        [setFAQs],
    );

    const AddFAQClick = useCallback(() => {
        setFAQs(faqs => [...faqs, { id: createNewGuid(), question: '', response: '', order: faqs.length + 1 }]);
    }, [setFAQs]);

    const { mutate } = useMutation(() => ApiCalls.helpFrequentQuestionsCreate(fAQs));

    const saveChanges = useCallback(() => {
        mutate();
    }, [mutate]);

    return (
        <div style={outerDivStyle}>
            <h3>FAQs</h3>
            <Separator>
                <Icon iconName="ProjectCollection" styles={separatorIconStyles} />
            </Separator>
            <br />
            <div style={{ display: 'inline-block' }}>
                <Stack tokens={{ childrenGap: 10 }}>
                    {fAQs.map(fAQ => {
                        return <AdminHelpFAQ key={fAQ.id} ReorderFAQ={ReorderFAQ} RemoveFAQ={RemoveFAQ} FAQUpdated={FAQUpdated} FAQ={fAQ} />;
                    })}
                    <Separator>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <Stack.Item align="center">
                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    <DefaultButton text="Add Question" onClick={AddFAQClick}></DefaultButton>
                                    <PrimaryButton onClick={saveChanges} text="Save Questions" />
                                </Stack>
                            </Stack.Item>
                        </Stack>
                    </Separator>
                </Stack>
                <br />
                <br />
                <br />
            </div>
        </div>
    );
}

export default AdminFAQs;

const outerDivStyle = {
    margin: '20px 0px 0px 0px',
    display: 'block',
    justifyContent: 'center',
    textAlign: 'center',
} as React.CSSProperties;

const separatorIconStyles: IIconStyles = {
    root: {
        fontSize: '24px',
        height: '24px',
        width: '24px',
    },
};
